import { map } from "rxjs";
import { inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Route, Router } from "@angular/router";

import { MembershipsService, PackagesService } from "../admin";
import { productRoutes } from "./components/ticket-forms/products";

import { EmptyDialogComponent } from "src/app/shared";
import { AuthService, EventServicesCodes, loaderContext, ParentServiceCode, TourServicesCodes } from "src/app/core";

interface ParentServiceRoutes extends Route {
  path: keyof typeof ParentServiceCode;
}

interface TourServiceRoutes extends Route {
  path: keyof typeof TourServicesCodes;
}

interface EventServiceRoutes extends Route {
  path: keyof typeof EventServicesCodes;
}

// -----------------------------------------------------------------------------------------------

/**
 * @description children routes of the tour services
 */
const childrenTourBookRoutes: TourServiceRoutes[] = [
  {
    path: "EntranceTicket",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/regular-ticket/regular-ticket.component").then((c) => c.RegularTicketComponent),
  },
  {
    path: "FreeEntry",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/free-entry/free-entry.component").then((c) => c.FreeEntryComponent),
  },
  {
    path: "GroupOffers",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/group-offers/group-offers.component").then((c) => c.GroupOffersComponent),
  },
  {
    path: "SchoolUniversityVisits",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () =>
      import("./components/ticket-forms/student-visit-ticket/student-visit-ticket.component").then((c) => c.StudentVisitTicketComponent),
  },
  {
    path: "Packages",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const dialog = inject(MatDialog);

        return inject(PackagesService)
          .getAll(loaderContext("packages"))
          .pipe(
            map((packages) => {
              if (packages.length) return true;

              dialog.open(EmptyDialogComponent, {
                panelClass: ["small", "not-fit-content", "h-max-content", "p-0"],
                autoFocus: false,
                disableClose: true,
                data: {
                  message: "ALERT.PACKAGES_TOUR_EMPTY",
                },
              });

              return false;
            }),
          );
      },
    ],
    loadComponent: () => import("./components/ticket-forms/packages/packages.component").then((c) => c.PackagesComponent),
  },
  {
    path: TourServicesCodes.GuidedTours,
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () => import("./components/ticket-forms/guided-tours/guided-tours.component").then((c) => c.GuidedToursComponent),
  },
  {
    path: "TourismCompanies",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const membershipsService = inject(MembershipsService);
        const authService = inject(AuthService);
        const router = inject(Router);

        return membershipsService.checkAddingService().pipe(
          map((res) => {
            const isAllowed = res === 1;

            if (!isAllowed || authService.isCompanyUser()) {
              router.navigate(["/services"]);
              return false;
            }

            return true;
          }),
        );
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/tourism-companies/tourism-companies.component").then((c) => c.TourismCompaniesComponent),
  },
  {
    path: "OfficialInstitutions",
    canActivate: [
      () => {
        if (inject(AuthService).isOfficialInstitutionUser() || inject(AuthService).isAdmin()) return true;

        inject(Router).navigate(["/services"]);
        return false;
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/official-institutions/official-institutions.component").then(
        (c) => c.OfficialInstitutionsComponent,
      ),
  },
  {
    path: "FriendsOfOmanMuseum",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    canActivate: [
      () => {
        const membershipsService = inject(MembershipsService);
        const router = inject(Router);

        return membershipsService.checkAddingService().pipe(
          map((res) => {
            const isAllowed = res === 1;

            if (!isAllowed) {
              router.navigate(["/services"]);
              return false;
            }

            return true;
          }),
        );
      },
    ],
    loadComponent: () =>
      import("./components/ticket-forms/friends-of-oman-museum/friends-of-oman-museum.component").then(
        (c) => c.FriendsOfOmanMuseumComponent,
      ),
  },
  {
    path: "Products",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    children: productRoutes,
  },
  {
    path: "FriendsOfOmanMuseum",
    canMatch: [() => !inject(AuthService).isOfficialInstitutionUser()],
    loadComponent: () =>
      import("./components/ticket-forms/friends-of-oman-museum/friends-of-oman-museum.component").then(
        (c) => c.FriendsOfOmanMuseumComponent,
      ),
  },
];

/**
 * @description children routes of the event services
 */
const childrenEventBookRoutes: EventServiceRoutes[] = [
  {
    path: "ExhibitionsAndEvents",
    children: [
      {
        path: "hold-event",
        title: "SERVICES.ExhibitionsAndEvents",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events-hold/exhibitions-and-events-hold.component").then(
            (c) => c.ExhibitionsAndEventsHoldComponent,
          ),
      },
      {
        path: "hold-event/:id",
        title: "SERVICES.ExhibitionsAndEvents",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events-hold/exhibitions-and-events-hold.component").then(
            (c) => c.ExhibitionsAndEventsHoldComponent,
          ),
      },
      {
        path: ":id",
        loadComponent: () =>
          import("./components/ticket-forms/exhibitions-and-events/exhibitions-and-events.component").then(
            (c) => c.ExhibitionsAndEventsComponent,
          ),
      },
    ],
  },
];

/**
 * @description main services categories routes.
 */
export const bookRoutes: ParentServiceRoutes[] = [
  {
    path: "TOUR",
    loadComponent: () => import("./pages/book/book.component").then((c) => c.BookComponent),
    children: childrenTourBookRoutes,
  },
  {
    path: "EVENT",
    loadComponent: () => import("./pages/book/book.component").then((c) => c.BookComponent),
    children: childrenEventBookRoutes,
  },
];
